/**
 * Filtered Metrics Hook
 * 
 * Provides metrics filtered by user (Sarah Johnson vs Everyone)
 * Uses modular mock data for easy maintenance
 */

import { useMemo } from 'react';
import { 
  getTodaysPriorities, 
  getAgingPortfolio, 
  getDisputeMetrics,
  getCollectionMetrics,
  getTodaysWins,
  getKeyMetrics
} from '@/mockData';

export interface FilteredMetrics {
  todaysPriorities: {
    casesDueToday: { count: number; value: number };
    unallocatedReceipts: { count: number; value: number };
    promisesDueToday: { count: number; value: number; brokenCount: number; brokenValue: number };
    disputesRequiringResponse: { count: number; value: number };
  };
  agingPortfolio: {
    current030: number;
    over3160: number;
    over6190: number;
    over91120: number;
    over121180: number;
    over180: number;
    over365: number;
    total: number;
  };
  disputeMetrics: {
    activeCount: number;
    totalAmount: number;
    avgResolutionTime: number;
    resolutionRate: number;
    byPriority: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
  };
  collectionMetrics: {
    totalDebt: number;
    collectedAmount: number;
    collectionRate: number;
    targetRate: number;
    variance: number;
  };
  todaysWins: {
    paymentsReceived: number;
    paymentsAmount: number;
    promisesKept: number;
    promisesAmount: number;
    disputesResolved: number;
    disputesAmount: number;
  };
  keyMetrics: {
    totalOutstanding: number;
    over90Days: number;
    over180Days: number;
    dso: number;
    collectionEfficiency: number;
    averageDebtAge: number;
    customerCount: number;
  };
}

/**
 * Get metrics filtered by user
 * @param filter - 'everyone' or user ID (e.g., 'user-001' for Sarah Johnson)
 */
export function useFilteredMetrics(filter: string = 'everyone'): FilteredMetrics {
  return useMemo(() => {
    const todaysPriorities = getTodaysPriorities(filter);
    const agingPortfolio = getAgingPortfolio(filter);
    const disputeMetrics = getDisputeMetrics(filter);
    const collectionMetrics = getCollectionMetrics(filter);
    const todaysWins = getTodaysWins(filter);
    const keyMetrics = getKeyMetrics(filter);
    
    return {
      todaysPriorities,
      agingPortfolio,
      disputeMetrics,
      collectionMetrics,
      todaysWins,
      keyMetrics
    };
  }, [filter]);
}
